import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/store/shared/supabase";
import { useSession } from "@/session/SessionProvider";
import { createUuid } from "@/store/shared/id";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import {
  TERRITORY_LEVEL_PLURALS,
  TERRITORY_LEVEL_LABELS,
  type TerritoryCommune,
  type TerritoryProvince,
  type TerritoryRegion,
  type TerritorySecteur,
} from "./types";

interface TerritoryContextValue {
  regions: TerritoryRegion[];
  provinces: TerritoryProvince[];
  communes: TerritoryCommune[];
  secteurs: TerritorySecteur[];
  /** Chargement initial des territoires depuis Supabase. */
  loading: boolean;
  /** Message d'erreur si le chargement territorial échoue. */
  error: string | null;
  /** Recharge les territoires depuis Supabase. */
  reload: () => void;
  addRegion: (nom: string) => TerritoryRegion;
  updateRegion: (id: string, nom: string) => void;
  removeRegion: (id: string) => void;
  addProvince: (nom: string, regionId: string) => TerritoryProvince;
  updateProvince: (id: string, patch: { nom?: string; regionId?: string }) => void;
  removeProvince: (id: string) => void;
  addCommune: (nom: string, provinceId: string) => TerritoryCommune;
  updateCommune: (id: string, patch: { nom?: string; provinceId?: string }) => void;
  removeCommune: (id: string) => void;
  addSecteur: (nom: string, communeId: string) => TerritorySecteur;
  updateSecteur: (id: string, patch: { nom?: string; communeId?: string }) => void;
  removeSecteur: (id: string) => void;
  getRegion: (id: string) => TerritoryRegion | undefined;
  getProvince: (id: string) => TerritoryProvince | undefined;
  getCommune: (id: string) => TerritoryCommune | undefined;
  getSecteur: (id: string) => TerritorySecteur | undefined;
  regionName: (id: string) => string;
  provinceName: (id: string) => string;
  communeName: (id: string) => string;
  secteurName: (id: string) => string;
  provincesOf: (regionId: string) => TerritoryProvince[];
  communesOf: (provinceId: string) => TerritoryCommune[];
  secteurOf: (communeId: string) => TerritorySecteur[];
  communesOfRegion: (regionId: string) => TerritoryCommune[];
  secteursOfProvince: (provinceId: string) => TerritorySecteur[];
  secteursOfRegion: (regionId: string) => TerritorySecteur[];
  /** Aides en cascade adressées par nom (filtres territoriaux). */
  regionNames: string[];
  getProvinceNames: (regionNom: string) => string[];
  getCommuneNames: (regionNom: string, provinceNom: string) => string[];
  getSecteurNames: (communeNom: string) => string[];
}

const TerritoryContext = createContext<TerritoryContextValue | null>(null);

type RegionRow = { id: string; nom: string };
type ProvinceRow = { id: string; region_id: string; nom: string };
type CommuneRow = { id: string; province_id: string; nom: string };
type SecteurRow = { id: string; commune_id: string; nom: string; type: string };

/**
 * Source de vérité du découpage territorial du Burkina Faso.
 *
 * Les données proviennent désormais de Supabase (tables `regions`, `provinces`,
 * `communes`, `secteurs`) : une seule requête au montage, aucune donnée locale
 * statique, aucun localStorage. Les identifiants exposés sont les UUID réels,
 * ce qui garantit la compatibilité avec les FK (`region_id`, `province_id`,
 * `commune_id`, `secteur_id`) attendues par la base.
 */
export function TerritoryProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const sessionKey = session?.id ?? "";
  const [regions, setRegions] = useState<TerritoryRegion[]>([]);
  const [provinces, setProvinces] = useState<TerritoryProvince[]>([]);
  const [communes, setCommunes] = useState<TerritoryCommune[]>([]);
  const [secteurs, setSecteurs] = useState<TerritorySecteur[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regionsRes, provincesRes, communesRes, secteursRes] = await Promise.all([
        supabase.from("regions").select("id, nom").order("nom", { ascending: true }),
        supabase.from("provinces").select("id, region_id, nom").order("nom", { ascending: true }),
        supabase.from("communes").select("id, province_id, nom").order("nom", { ascending: true }),
        supabase.from("secteurs").select("id, commune_id, nom, type").order("nom", { ascending: true }),
      ]);

      const firstError =
        regionsRes.error || provincesRes.error || communesRes.error || secteursRes.error || null;
      if (firstError) {
        throw new Error(firstError.message);
      }

      setRegions(((regionsRes.data ?? []) as RegionRow[]).map((row) => ({ id: row.id, nom: row.nom })));
      setProvinces(
        ((provincesRes.data ?? []) as ProvinceRow[]).map((row) => ({
          id: row.id,
          nom: row.nom,
          regionId: row.region_id,
        }))
      );
      setCommunes(
        ((communesRes.data ?? []) as CommuneRow[]).map((row) => ({
          id: row.id,
          nom: row.nom,
          provinceId: row.province_id,
        }))
      );
      setSecteurs(
        ((secteursRes.data ?? []) as SecteurRow[]).map((row) => ({
          id: row.id,
          nom: row.nom,
          communeId: row.commune_id,
        }))
      );
    } catch (err) {
      // Aucune donnée fictive en cas d'erreur : on vide les listes.
      setRegions([]);
      setProvinces([]);
      setCommunes([]);
      setSecteurs([]);
      setError(
        err instanceof Error && err.message
          ? err.message
          : "Impossible de charger les données territoriales depuis la base."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  // Recharge les territoires à chaque changement de session (connexion /
  // déconnexion) : les tables sont lisibles par les comptes authentifiés, donc
  // il faut rejouer la requête une fois la session établie.
  useEffect(() => {
    void load();
  }, [load, sessionKey]);

  /* -------------------------------------------------------------- */
  /* Écritures (persistées dans Supabase)                            */
  /* -------------------------------------------------------------- */

  /**
   * Exécute une écriture Supabase puis recharge la source de vérité.
   * En cas d'échec (RLS, contrainte…), l'état optimiste est corrigé et
   * l'erreur est remontée à l'utilisateur — jamais masquée.
   */
  const persistWrite = useCallback(
    (action: () => PromiseLike<{ error: unknown }>, onFailure: () => void, message: string) => {
      void action()
        .then(({ error }) => {
          if (error) {
            onFailure();
            dispatchDataError({ scope: "Territoires", message, technical: describeSupabaseError(error) });
            return;
          }
          void load();
        })
        .catch((caught) => {
          onFailure();
          dispatchDataError({ scope: "Territoires", message, technical: describeSupabaseError(caught) });
        });
    },
    [load]
  );

  /* -------------------------------------------------------------- */
  /* Régions                                                         */
  /* -------------------------------------------------------------- */
  const addRegion = useCallback(
    (nom: string) => {
      const region: TerritoryRegion = { id: createUuid(), nom: nom.trim() };
      setRegions((prev) => [...prev, region].sort((a, b) => a.nom.localeCompare(b.nom, "fr")));
      persistWrite(
        () => supabase.from("regions").insert({ nom: region.nom }),
        () => setRegions((prev) => prev.filter((item) => item.id !== region.id)),
        "Impossible d'enregistrer la région."
      );
      return region;
    },
    [persistWrite]
  );

  const updateRegion = useCallback(
    (id: string, nom: string) => {
      setRegions((prev) => prev.map((item) => (item.id === id ? { ...item, nom: nom.trim() } : item)));
      persistWrite(
        () => supabase.from("regions").update({ nom: nom.trim() }).eq("id", id),
        () => void load(),
        "Impossible de modifier la région."
      );
    },
    [persistWrite, load]
  );

  const removeRegion = useCallback(
    (id: string) => {
      setRegions((prev) => prev.filter((item) => item.id !== id));
      persistWrite(
        () => supabase.from("regions").delete().eq("id", id),
        () => void load(),
        "Impossible de supprimer la région."
      );
    },
    [persistWrite, load]
  );

  /* -------------------------------------------------------------- */
  /* Provinces                                                       */
  /* -------------------------------------------------------------- */
  const addProvince = useCallback(
    (nom: string, regionId: string) => {
      const province: TerritoryProvince = { id: createUuid(), nom: nom.trim(), regionId };
      setProvinces((prev) => [...prev, province]);
      persistWrite(
        () => supabase.from("provinces").insert({ nom: province.nom, region_id: regionId }),
        () => setProvinces((prev) => prev.filter((item) => item.id !== province.id)),
        "Impossible d'enregistrer la province."
      );
      return province;
    },
    [persistWrite]
  );

  const updateProvince = useCallback(
    (id: string, patch: { nom?: string; regionId?: string }) => {
      setProvinces((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                nom: patch.nom !== undefined ? patch.nom.trim() : item.nom,
                regionId: patch.regionId ?? item.regionId,
              }
            : item
        )
      );
      const payload: Record<string, unknown> = {};
      if (patch.nom !== undefined) payload.nom = patch.nom.trim();
      if (patch.regionId !== undefined) payload.region_id = patch.regionId;
      persistWrite(
        () => supabase.from("provinces").update(payload).eq("id", id),
        () => void load(),
        "Impossible de modifier la province."
      );
    },
    [persistWrite, load]
  );

  const removeProvince = useCallback(
    (id: string) => {
      setProvinces((prev) => prev.filter((item) => item.id !== id));
      persistWrite(
        () => supabase.from("provinces").delete().eq("id", id),
        () => void load(),
        "Impossible de supprimer la province."
      );
    },
    [persistWrite, load]
  );

  /* -------------------------------------------------------------- */
  /* Communes / Villes                                               */
  /* -------------------------------------------------------------- */
  const addCommune = useCallback(
    (nom: string, provinceId: string) => {
      const commune: TerritoryCommune = { id: createUuid(), nom: nom.trim(), provinceId };
      setCommunes((prev) => [...prev, commune]);
      persistWrite(
        () => supabase.from("communes").insert({ nom: commune.nom, province_id: provinceId }),
        () => setCommunes((prev) => prev.filter((item) => item.id !== commune.id)),
        "Impossible d'enregistrer la commune."
      );
      return commune;
    },
    [persistWrite]
  );

  const updateCommune = useCallback(
    (id: string, patch: { nom?: string; provinceId?: string }) => {
      setCommunes((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                nom: patch.nom !== undefined ? patch.nom.trim() : item.nom,
                provinceId: patch.provinceId ?? item.provinceId,
              }
            : item
        )
      );
      const payload: Record<string, unknown> = {};
      if (patch.nom !== undefined) payload.nom = patch.nom.trim();
      if (patch.provinceId !== undefined) payload.province_id = patch.provinceId;
      persistWrite(
        () => supabase.from("communes").update(payload).eq("id", id),
        () => void load(),
        "Impossible de modifier la commune."
      );
    },
    [persistWrite, load]
  );

  const removeCommune = useCallback(
    (id: string) => {
      setCommunes((prev) => prev.filter((item) => item.id !== id));
      persistWrite(
        () => supabase.from("communes").delete().eq("id", id),
        () => void load(),
        "Impossible de supprimer la commune."
      );
    },
    [persistWrite, load]
  );

  /* -------------------------------------------------------------- */
  /* Secteurs / Quartiers                                            */
  /* -------------------------------------------------------------- */
  const addSecteur = useCallback(
    (nom: string, communeId: string) => {
      const secteur: TerritorySecteur = { id: createUuid(), nom: nom.trim(), communeId };
      setSecteurs((prev) => [...prev, secteur]);
      persistWrite(
        () => supabase.from("secteurs").insert({ nom: secteur.nom, commune_id: communeId }),
        () => setSecteurs((prev) => prev.filter((item) => item.id !== secteur.id)),
        "Impossible d'enregistrer le secteur."
      );
      return secteur;
    },
    [persistWrite]
  );

  const updateSecteur = useCallback(
    (id: string, patch: { nom?: string; communeId?: string }) => {
      setSecteurs((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                nom: patch.nom !== undefined ? patch.nom.trim() : item.nom,
                communeId: patch.communeId ?? item.communeId,
              }
            : item
        )
      );
      const payload: Record<string, unknown> = {};
      if (patch.nom !== undefined) payload.nom = patch.nom.trim();
      if (patch.communeId !== undefined) payload.commune_id = patch.communeId;
      persistWrite(
        () => supabase.from("secteurs").update(payload).eq("id", id),
        () => void load(),
        "Impossible de modifier le secteur."
      );
    },
    [persistWrite, load]
  );

  const removeSecteur = useCallback(
    (id: string) => {
      setSecteurs((prev) => prev.filter((item) => item.id !== id));
      persistWrite(
        () => supabase.from("secteurs").delete().eq("id", id),
        () => void load(),
        "Impossible de supprimer le secteur."
      );
    },
    [persistWrite, load]
  );

  /* -------------------------------------------------------------- */
  /* Lectures et relations                                           */
  /* -------------------------------------------------------------- */
  const getRegion = useCallback((id: string) => regions.find((item) => item.id === id), [regions]);
  const getProvince = useCallback((id: string) => provinces.find((item) => item.id === id), [provinces]);
  const getCommune = useCallback((id: string) => communes.find((item) => item.id === id), [communes]);
  const getSecteur = useCallback((id: string) => secteurs.find((item) => item.id === id), [secteurs]);

  const regionName = useCallback((id: string) => regions.find((item) => item.id === id)?.nom ?? "", [regions]);
  const provinceName = useCallback((id: string) => provinces.find((item) => item.id === id)?.nom ?? "", [provinces]);
  const communeName = useCallback((id: string) => communes.find((item) => item.id === id)?.nom ?? "", [communes]);
  const secteurName = useCallback((id: string) => secteurs.find((item) => item.id === id)?.nom ?? "", [secteurs]);

  const provincesOf = useCallback(
    (regionId: string) => provinces.filter((item) => item.regionId === regionId),
    [provinces]
  );
  const communesOf = useCallback(
    (provinceId: string) => communes.filter((item) => item.provinceId === provinceId),
    [communes]
  );
  const secteurOf = useCallback(
    (communeId: string) => secteurs.filter((item) => item.communeId === communeId),
    [secteurs]
  );

  const provinceIdsOfRegion = useCallback(
    (regionId: string) => new Set(provinces.filter((item) => item.regionId === regionId).map((item) => item.id)),
    [provinces]
  );
  const communeIdsOfProvince = useCallback(
    (provinceId: string) => new Set(communes.filter((item) => item.provinceId === provinceId).map((item) => item.id)),
    [communes]
  );

  const communesOfRegion = useCallback(
    (regionId: string) => {
      const provinceIds = provinceIdsOfRegion(regionId);
      return communes.filter((item) => provinceIds.has(item.provinceId));
    },
    [communes, provinceIdsOfRegion]
  );

  const secteursOfProvince = useCallback(
    (provinceId: string) => {
      const communeIds = communeIdsOfProvince(provinceId);
      return secteurs.filter((item) => communeIds.has(item.communeId));
    },
    [secteurs, communeIdsOfProvince]
  );

  const secteursOfRegion = useCallback(
    (regionId: string) => {
      const communeIds = new Set(communesOfRegion(regionId).map((item) => item.id));
      return secteurs.filter((item) => communeIds.has(item.communeId));
    },
    [secteurs, communesOfRegion]
  );

  const regionNames = useMemo(() => regions.map((item) => item.nom), [regions]);

  const getProvinceNames = useCallback(
    (regionNom: string) => {
      const region = regions.find((item) => item.nom === regionNom);
      if (!region) return [];
      return provincesOf(region.id).map((item) => item.nom);
    },
    [regions, provincesOf]
  );

  const getCommuneNames = useCallback(
    (regionNom: string, provinceNom: string) => {
      const region = regions.find((item) => item.nom === regionNom);
      if (!region) return [];
      const province = provinces.find((item) => item.nom === provinceNom && item.regionId === region.id);
      if (!province) return [];
      return communesOf(province.id).map((item) => item.nom);
    },
    [regions, provinces, communesOf]
  );

  const getSecteurNames = useCallback(
    (communeNom: string) => {
      const commune = communes.find((item) => item.nom === communeNom);
      if (!commune) return [];
      return secteurOf(commune.id).map((item) => item.nom);
    },
    [communes, secteurOf]
  );

  const value = useMemo(
    () => ({
      regions,
      provinces,
      communes,
      secteurs,
      loading,
      error,
      reload: () => {
        void load();
      },
      addRegion,
      updateRegion,
      removeRegion,
      addProvince,
      updateProvince,
      removeProvince,
      addCommune,
      updateCommune,
      removeCommune,
      addSecteur,
      updateSecteur,
      removeSecteur,
      getRegion,
      getProvince,
      getCommune,
      getSecteur,
      regionName,
      provinceName,
      communeName,
      secteurName,
      provincesOf,
      communesOf,
      secteurOf,
      communesOfRegion,
      secteursOfProvince,
      secteursOfRegion,
      regionNames,
      getProvinceNames,
      getCommuneNames,
      getSecteurNames,
    }),
    [
      regions,
      provinces,
      communes,
      secteurs,
      loading,
      error,
      load,
      addRegion,
      updateRegion,
      removeRegion,
      addProvince,
      updateProvince,
      removeProvince,
      addCommune,
      updateCommune,
      removeCommune,
      addSecteur,
      updateSecteur,
      removeSecteur,
      getRegion,
      getProvince,
      getCommune,
      getSecteur,
      regionName,
      provinceName,
      communeName,
      secteurName,
      provincesOf,
      communesOf,
      secteurOf,
      communesOfRegion,
      secteursOfProvince,
      secteursOfRegion,
      regionNames,
      getProvinceNames,
      getCommuneNames,
      getSecteurNames,
    ]
  );

  return <TerritoryContext.Provider value={value}>{children}</TerritoryContext.Provider>;
}

export function useTerritory(): TerritoryContextValue {
  const context = useContext(TerritoryContext);
  if (!context) {
    throw new Error("useTerritory doit être utilisé à l'intérieur de TerritoryProvider");
  }
  return context;
}

// Réexports pratiques pour les écrans territoriaux.
export { TERRITORY_LEVEL_LABELS, TERRITORY_LEVEL_PLURALS };
export type { TerritoryCommune, TerritoryProvince, TerritoryRegion, TerritorySecteur, TerritoryLevel } from "./types";